import { newYorkDate } from '../districts/season.js';

/**
 * Per-holiday show names. Some houses decorate for Halloween and Christmas
 * under different names. A dedupe merge kept one name for the merged display;
 * holiday_names keeps both so each season shows its own.
 */

export type HolidayNames = Record<string, string>;

type KnownHoliday = 'Halloween' | 'Christmas';

/** Which holiday a listing's external_source implies on its own. */
export const SOURCE_HOLIDAY: Record<string, KnownHoliday> = {
  frightmaps: 'Halloween',
  socalhauntlist: 'Halloween',
  christmasprism: 'Christmas',
  christmaslightfinder: 'Christmas',
  californiachristmaslights: 'Christmas',
  long_island_2025: 'Christmas',
  'The Holiday Atlas': 'Christmas',
  'Hampton Roads VA Christmas Lights': 'Christmas',
};

export interface NamedRow {
  name: string | null;
  holidays: string[] | null;
  external_source: string | null;
}

/** 'christmas' -> 'Christmas'. */
export function titleCaseHoliday(value: string): string {
  const v = value.trim();
  return v ? v[0].toUpperCase() + v.slice(1).toLowerCase() : '';
}

const nameKey = (name: string) => name.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

const STREET_WORD = /\b(?:dr|drive|st|street|ave|avenue|rd|road|ln|lane|ct|court|trl|trail|way|blvd|boulevard|cir|circle|pl|place|pkwy|parkway|ter|terrace|hwy|highway)\b\.?/i;

/** "56 Roy Dr" or "3050 Beaver Pond Trail, Valrico, FL" is an address someone typed as a name, not a show name. */
export const looksLikeAddress = (name: string) => /^\d+[A-Za-z]?\s/.test(name.trim()) && STREET_WORD.test(name);

function ownHoliday(row: NamedRow): string | null {
  const own = [...new Set((row.holidays ?? []).map(titleCaseHoliday).filter(Boolean))];
  if (own.length === 1) return own[0];
  return SOURCE_HOLIDAY[row.external_source ?? ''] ?? null;
}

/**
 * Names for a merged display. Only a winner with 2+ holidays gets any. The
 * winner's name goes to its own holiday, each loser's name to the holiday that
 * loser stood for, and anything left takes the winner's name. Returns null
 * unless at least two different names come out.
 */
export function computeHolidayNames(winner: NamedRow, losers: readonly NamedRow[]): HolidayNames | null {
  const holidays = [...new Set((winner.holidays ?? []).map(titleCaseHoliday).filter(Boolean))];
  const winnerName = winner.name?.trim() ?? '';
  if (holidays.length < 2 || !winnerName) return null;

  const names: HolidayNames = {};
  const mine = ownHoliday(winner);
  if (mine && holidays.includes(mine)) names[mine] = winnerName;

  const sorted = [...losers].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  for (const loser of sorted) {
    const h = ownHoliday(loser);
    const name = loser.name?.trim();
    if (h && holidays.includes(h) && !names[h] && name && !looksLikeAddress(name)) names[h] = name;
  }
  for (const h of holidays) names[h] ??= winnerName;

  return new Set(Object.values(names).map(nameKey)).size >= 2 ? names : null;
}

/** Halloween Aug 15 to Oct 31, Christmas Nov 1 to Jan 6 (New York time), else none. */
function seasonHoliday(now: Date): KnownHoliday | null {
  const { month, day } = newYorkDate(now);
  if ((month === 8 && day >= 15) || month === 9 || month === 10) return 'Halloween';
  if (month >= 11 || (month === 1 && day <= 6)) return 'Christmas';
  return null;
}

export interface HolidayNamed {
  name: string;
  holiday_names?: HolidayNames | null;
}

/** The name to show: the asked-for holiday's, else this season's, else display.name. */
export function nameForHoliday(display: HolidayNamed, holiday?: string | null, now: Date = new Date()): string {
  const names = display.holiday_names;
  if (!names) return display.name;
  const wanted = holiday ? titleCaseHoliday(holiday) : seasonHoliday(now);
  if (!wanted) return display.name;
  const hit = Object.entries(names).find(([key]) => titleCaseHoliday(key) === wanted)?.[1];
  return hit?.trim() || display.name;
}

const ORDER = ['Christmas', 'Halloween'];

/** Every holiday name, Christmas then Halloween then the rest; [] when none. */
export function holidayNameList(display: Pick<HolidayNamed, 'holiday_names'>): Array<{ holiday: string; name: string }> {
  const names = display.holiday_names;
  if (!names) return [];
  const rank = (h: string) => {
    const i = ORDER.indexOf(h);
    return i === -1 ? ORDER.length : i;
  };
  return Object.entries(names)
    .map(([holiday, name]) => ({ holiday: titleCaseHoliday(holiday), name }))
    .filter((e) => e.name)
    .sort((a, b) => rank(a.holiday) - rank(b.holiday) || a.holiday.localeCompare(b.holiday));
}
